<template>
  <div class="page-plans">
    <Overlay
      v-if="invoiceLoaded"
      width="small"
      autoHeight
      :link="{name: 'invoices'}"
    >
      <Notification
        v-if="notification.active"
        :content="notification.content"
        :type="notification.type"
      />

      <h1 class="overlay-heading">
        Invoice {{ invoice.number }}
        <span class="invoice-date">{{ date(invoice.created) }}</span>
      </h1>

      <div>
        <table class="invoice-table">
          <thead>
            <tr>
              <td>Description</td>
              <td>Qty</td>
              <td>Unit Price</td>
              <td class="table-price">Amount</td>
            </tr>
          </thead>

          <tbody class="line-items">
            <tr v-for="line in invoice.lines.data" :key="line.id">
              <td>
                {{ line.description }}<br />
                <span v-if="line.type === 'subscription'">
                  ({{ date(line.period.start) }} - {{ date(line.period.end) }})
                </span>
              </td>
              <td>{{ line.quantity }}</td>
              <td>{{ price(line.price.unit_amount) }}</td>
              <td class="table-price">{{ price(line.amount) }}</td>
            </tr>
          </tbody>

          <tbody>
            <tr class="no-border">
              <td></td>
              <td></td>
              <td>
                <strong>Subtotal</strong>
              </td>
              <td class="table-price">
                <strong>{{ price(invoice.subtotal) }}</strong>
              </td>
            </tr>

            <tr class="no-border">
              <td></td>
              <td></td>
              <td>
                <strong>Total</strong>
              </td>
              <td class="table-price">
                <strong>{{ price(invoice.total) }}</strong>
              </td>
            </tr>

            <tr class="no-border">
              <td></td>
              <td></td>
              <td>
                Applied Balance
              </td>
              <td class="table-price">
                {{ price(invoice.starting_balance) }}
              </td>
            </tr>

            <tr class="no-border">
              <td></td>
              <td></td>
              <td>
                Amount Paid
              </td>
              <td class="table-price">
                {{ price(invoice.amount_paid) }}
              </td>
            </tr>

            <tr class="no-border">
              <td></td>
              <td></td>
              <td>
                <strong>Amount Due</strong>
              </td>
              <td class="table-price">
                <strong>{{ price(invoice.amount_remaining) }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-if="updatedSubscription">
        Congratulations! Your plan has been successfully updated.
      </p>

      <div v-if="invoice.paid">
        <p class="invoice-status invoice-paid">
          Invoice Paid, Thank you!
        </p>

        <div class="payment-controls">
          <a target="_blank" class="btn is-subtle" :href="invoice.invoice_pdf">
            Download PDF
          </a>

          <router-link
            v-if="siteId"
            class="btn is-primary"
            :to="{name: 'site', params: {siteId: siteId}}"
          >
            Manage Website
          </router-link>

          <router-link
            v-if="isDomainInvoice"
            class="btn is-primary"
            :to="{name: 'company-domains', params: {companyId: companyId}}"
          >
            View My Domains
          </router-link>
        </div>
      </div>

      <div v-else>
        <p class="invoice-status invoice-not-paid">Invoice Not Paid</p>
        <div v-if="isDomainInvoice">
          <p>
            We strongly advise that this invoice is paid as quickly as possible,
            as we cannot hold domain names and therefore cannot guarantee that
            it'll remain available.
          </p>
          <p>If you'd like to amend this invoice, please contact our team.</p>
        </div>

        <div class="pay-invoice">
          <div v-if="hasDefaultPaymentMethod">
            <h2>How would you like to pay?</h2>
            <p>Please select which card you'd like to make the payment with.</p>

            <div class="invoice-payment-card">
              <PaymentCard
                v-for="card in paymentMethods"
                :key="card.id"
                :card="card"
                :customer="customer"
                :selectedId="paymentMethodId"
                showSelectCard
                @selectCard="setPaymentMethod"
              />
            </div>

            <div class="payment-controls">
              <router-link
                class="btn is-subtle"
                :to="{
                  name: 'add-payment-method',
                  query: {returnPath: this.$route.fullPath}
                }"
              >
                Add a payment method
              </router-link>

              <Btn
                primary
                pulse
                v-if="paymentMethodId"
                @click="payInvoice"
                :isLoading="payBtnLoading"
              >
                Make Payment ({{ price(invoice.amount_remaining) }})
              </Btn>
            </div>
          </div>

          <div v-else>
            <h2>No payment methods found</h2>
            <p>
              It doesn't look like you have added a payment method yet. Not to
              worry, it'll only take a sec and we'll bring you back to this
              invoice once you've added one.
            </p>

            <router-link
              class="btn is-tertiary"
              :to="{
                name: 'add-payment-method',
                query: {returnPath: this.$route.fullPath}
              }"
            >
              Add a payment method
            </router-link>
          </div>
        </div>
      </div>
    </Overlay>
  </div>
</template>

<script>
import moment from 'moment'

import {readStripeCustomerData} from '@/lib/payments/customers'
import {readCustomerPaymentMethodsData} from '@/lib/payments/payment_methods'

import {
  readCustomerInvoiceData,
  createCustomerInvoicePaymentData
} from '@/lib/payments/invoices'

import notification from '@/mixins/notification'
import page from '@/mixins/page'
import Overlay from '@/components/Overlay'
import Btn from '@/components/Btn'
import PaymentCard from '@/components/PaymentCard'

export default {
  name: 'invoice',

  mixins: [notification, page],

  data() {
    return {
      invoiceLoaded: false,
      invoice: {},
      customer: {},
      paymentMethods: [],
      paymentMethodId: '',
      siteId: '',
      payBtnLoading: false
    }
  },

  props: {
    companyId: String,
    invoiceId: String
  },

  components: {
    Overlay,
    Btn,
    PaymentCard
  },

  methods: {
    getData() {
      readCustomerInvoiceData(this.companyId, this.invoiceId)
        .then(({data}) => {
          this.invoice = data
          this.siteId = data.lines.data[0].metadata.site_id || false
          this.invoiceLoaded = true
        })
        .catch(({response}) => {})

      readCustomerPaymentMethodsData(this.companyId)
        .then(({data}) => {
          this.paymentMethods = data.data
        })
        .catch(({response}) => {})

      readStripeCustomerData(this.companyId).then(({data}) => {
        this.customer = data
      })
    },

    setPaymentMethod(id) {
      this.paymentMethodId = id
    },

    payInvoice() {
      this.payBtnLoading = true

      let paymentMethodId = {
        payment_method_id: this.paymentMethodId
      }

      createCustomerInvoicePaymentData(
        this.companyId,
        this.invoiceId,
        paymentMethodId
      ).then(({data}) => {
        this.triggerNotification('success', 'invoice', 'paid', true)
        this.payBtnLoading = false

        window.setTimeout(() => {
          this.getData()
        }, this.closeDelay)
      })
    }
  },

  computed: {
    title() {
      return this.invoice.number ? `Invoice ${this.invoice.number}` : 'Invoice'
    },

    date() {
      return (date) => {
        return moment.unix(date).format('dddd DD MMM YYYY')
      }
    },

    price() {
      return (price) => {
        let amount = price / 100

        if (amount >= 0) {
          return '£' + amount
        } else {
          return amount.toString().replace('-', '-£')
        }
      }
    },

    hasDefaultPaymentMethod() {
      return this.customer.invoice_settings
        ? this.customer.invoice_settings.default_payment_method
        : false
    },

    updatedSubscription() {
      return !!this.$route.query.updatedSubscription
    },

    isDomainInvoice() {
      return !!this.$route.query.domain
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass" scoped>
@import '../../stylesheets/base/variables'
@import '../../stylesheets/helpers/mixins'
@import '../../stylesheets/helpers/extends'

.invoice-table
  width: 100%
  border-collapse: collapse

  thead
    font-weight: bold

    td
      border: none

  .table-price
    text-align: right

  .line-items
    tr
      &:last-child
        td
          border-bottom: 1px solid $color-border

  td
    padding: $gap-small 0
    border-top: 1px solid $color-border

  .no-border
    td
      border: none
      padding: 10px 0

    &:first-child
      td
        padding-top: $gap-small

.pay-invoice
  background: rgba(0, 0, 0, 0.04)
  padding: $gap-small
  margin-top: $gap-large
  border-radius: 5px

  h2
    font-size: 16px
    margin-bottom: $gap-medium

.payment-controls
  display: flex
  justify-content: space-between

.invoice-payment-card
  margin: $gap-large 0 $gap-x-large

.invoice-status
  font-weight: bold
  text-transform: uppercase

.invoice-not-paid
  color: $color-error

.invoice-paid
  color: $color-active

.invoice-date
  opacity: 0.5
  font-size: 10px
  display: block
</style>
